import React, { forwardRef, useImperativeHandle } from 'react';
import { SplitAndClassifyRequest, DocumentTypeDefinition } from '../types';
import { features } from '../config';

interface FormDataWithAuth extends Omit<SplitAndClassifyRequest, 'asset_id'> {
  auth_token: string;
}

interface SplitAndClassifyRequestFormProps {
  formData: FormDataWithAuth;
  onFormDataChange: (formData: FormDataWithAuth) => void;
  onSubmit: (request: Omit<SplitAndClassifyRequest, 'asset_id'>, authToken: string) => void;
  disabled?: boolean;
  onValidationChange?: (isValid: boolean) => void;
}

export interface SplitAndClassifyRequestFormRef {
  isValid: () => boolean;
}

const DOCUMENT_TYPE_TEMPLATES = {
  shipment: {
    displayName: 'Shipment (B/L)',
    definitions: [
      { name: 'Bill of Lading' },
      { name: 'Container Manifest' },
      { name: 'Cargo Receipt' },
      { name: 'Invoice' },
      { name: 'Packing List' },
      { name: 'Safety Data Sheet' },
      { 
        name: 'Hazard Report',
        definition: 'Also known as Hazard Classification and Identification Report for Transport of Goods'
      },
      { name: 'Exporter Statement' },
    ]
  },
  financial: {
    displayName: 'Financial Documents',
    definitions: [
      { name: 'Invoice' },
      { name: 'Receipt' },
      { name: 'Bank Statement' },
      { name: 'Purchase Order' }
    ]
  },
  legal: {
    displayName: 'Legal Documents',
    definitions: [
      { name: 'Contract' },
      { name: 'Agreement' },
      { name: 'Legal Notice' },
      { name: 'Certificate' }
    ]
  },
  hr: {
    displayName: 'HR Documents',
    definitions: [
      { name: 'Resume' },
      { name: 'Job Application' },
      { name: 'Employment Contract' },
      { name: 'Performance Review' }
    ]
  },
  medical: {
    displayName: 'Medical Documents',
    definitions: [
      { name: 'Medical Report' },
      { name: 'Prescription' },
      { name: 'Lab Results' },
      { name: 'Insurance Claim' }
    ]
  }
};

export const SplitAndClassifyRequestForm = forwardRef<SplitAndClassifyRequestFormRef, SplitAndClassifyRequestFormProps>(({ 
  formData,
  onFormDataChange,
  onSubmit,
  disabled,
  onValidationChange
}, ref) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const { auth_token, ...requestData } = formData;
    // Only include args in the request when enabled; otherwise send null
    const payload = {
      ...requestData,
      chat_complete_request_args: chatArgsEnabled ? requestData.chat_complete_request_args : null,
    };
    onSubmit(payload, auth_token);
  };

  const updateFormData = (updates: Partial<FormDataWithAuth>) => {
    onFormDataChange({ ...formData, ...updates });
  };

  const addDocumentType = () => {
    updateFormData({
      document_type_definitions: [...formData.document_type_definitions, { name: '' }]
    });
  };

  const updateDocumentType = (index: number, field: keyof DocumentTypeDefinition, value: string) => {
    const updatedDefinitions = formData.document_type_definitions.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    updateFormData({ document_type_definitions: updatedDefinitions });
  };

  const removeDocumentType = (index: number) => {
    const updatedDefinitions = formData.document_type_definitions.filter((_, i) => i !== index);
    updateFormData({ document_type_definitions: updatedDefinitions });
  };

  const removeAllDocumentTypes = () => {
    updateFormData({ document_type_definitions: [{ name: '' }] });
  };

  const loadTemplate = (templateKey: keyof typeof DOCUMENT_TYPE_TEMPLATES) => {
    updateFormData({ document_type_definitions: DOCUMENT_TYPE_TEMPLATES[templateKey].definitions });
  };

  // Custom Chat Completion Args (JSON) state
  const [chatArgsEnabled, setChatArgsEnabled] = React.useState(!!formData.chat_complete_request_args);
  const [chatArgsText, setChatArgsText] = React.useState(
    formData.chat_complete_request_args ? JSON.stringify(formData.chat_complete_request_args, null, 2) : ''
  );
  const [chatArgsError, setChatArgsError] = React.useState<string | null>(null);

  const handleChatArgsChange = (text: string) => {
    setChatArgsText(text);
    if (text.trim() === '') {
      setChatArgsError(null);
      // Keep cache empty if user clears it explicitly
      updateFormData({ chat_complete_request_args: null });
      return;
    }
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        setChatArgsError(null);
        updateFormData({ chat_complete_request_args: parsed });
      } else {
        setChatArgsError('JSON must be an object (e.g., { "temperature": 0.2 })');
      }
    } catch {
      setChatArgsError('Invalid JSON');
    }
  };

  // Add: handle Tab indentation inside textarea
  const handleChatArgsKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Tab') return;
    e.preventDefault();
    const target = e.currentTarget;
    const start = target.selectionStart ?? 0;
    const end = target.selectionEnd ?? 0;

    if (e.shiftKey) {
      // Unindent: remove up to two leading spaces immediately before caret
      const before = chatArgsText.slice(0, start);
      const after = chatArgsText.slice(end);
      let remove = 0;
      if (before.endsWith('  ')) remove = 2;
      else if (before.endsWith('\t')) remove = 1;
      if (remove > 0) {
        const newValue = chatArgsText.slice(0, start - remove) + after;
        setChatArgsText(newValue);
        handleChatArgsChange(newValue);
        requestAnimationFrame(() => {
          const pos = start - remove;
          target.setSelectionRange(pos, pos);
        });
      }
      return;
    }

    // Indent with two spaces
    const indent = '  ';
    const newValue = chatArgsText.slice(0, start) + indent + chatArgsText.slice(end);
    setChatArgsText(newValue);
    handleChatArgsChange(newValue);
    requestAnimationFrame(() => {
      const pos = start + indent.length;
      target.setSelectionRange(pos, pos);
    });
  };

  React.useEffect(() => {
    // Do not clear cached JSON when toggling off; just clear the error
    if (!chatArgsEnabled) {
      setChatArgsError(null);
    }
  }, [chatArgsEnabled]);

  const isFormValid = React.useCallback(() => {
    return (
      formData.endpoint.trim() !== '' &&
      formData.token.trim() !== '' &&
      formData.workspace_or_extractor_id.trim() !== '' &&
      formData.document_type_definitions.some(def => def.name.trim() !== '') &&
      (!chatArgsEnabled || chatArgsError === null)
    );
  }, [formData, chatArgsEnabled, chatArgsError]);

  React.useEffect(() => {
    // Notify parent about validation state changes
    if (onValidationChange) {
      onValidationChange(isFormValid());
    }
  }, [formData, chatArgsEnabled, chatArgsError, onValidationChange, isFormValid]);

  // Expose validation method to parent via ref
  useImperativeHandle(ref, () => ({
    isValid: isFormValid
  }), [isFormValid]);

  return (
    <form onSubmit={handleSubmit} className="request-form">
      <div className="form-section">
        <h3>Authentication</h3>
        <div className="form-group">
          <label htmlFor="auth-token">Auth Token:</label>
          <input
            id="auth-token"
            type="password"
            value={formData.auth_token}
            onChange={(e) => updateFormData({ auth_token: e.target.value })}
            placeholder="Enter your auth token"
            disabled={disabled}
          />
        </div>
      </div>

      <div className="form-section">
        <h3>FormX Configuration</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Endpoint *</label>
            <input
              type="url"
              value={formData.endpoint}
              onChange={(e) => updateFormData({ endpoint: e.target.value })}
              placeholder="https://api.formx.ai"
              required
              disabled={disabled}
            />
          </div>
          <div className="form-group">
            <label>Worker Token *</label>
            <input
              type="password"
              value={formData.token}
              onChange={(e) => updateFormData({ token: e.target.value })}
              placeholder="Your FormX worker token"
              required
              disabled={disabled}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Workspace/Extractor ID *</label>
            <input
              type="text"
              value={formData.workspace_or_extractor_id}
              onChange={(e) => updateFormData({ workspace_or_extractor_id: e.target.value })}
              placeholder="workspace_id or extractor_id"
              required
              disabled={disabled}
            />
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.is_workspace}
                onChange={(e) => updateFormData({ is_workspace: e.target.checked })}
                disabled={disabled}
              />
              Is Workspace
            </label>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>LLM Configuration</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Model Provider</label>
            <select
              value={formData.llm_model_provider}
              onChange={(e) => updateFormData({ llm_model_provider: e.target.value as 'openrouter' | 'google' })}
              disabled={disabled}
            >
              <option value="openrouter">OpenRouter</option>
              <option value="google">Google</option>
            </select>
          </div>
          <div className="form-group">
            <label>Model Name</label>
            <input
              type="text"
              value={formData.llm_model_name || ''}
              onChange={(e) => updateFormData({ llm_model_name: e.target.value || null })}
              placeholder="e.g., gpt-4"
              disabled={disabled}
            />
          </div>
        </div>
        {formData.llm_model_provider === 'openrouter' && (
          <div className="form-row form-group">
            <label>OpenRouter API Key</label>
            <input
              type="password"
              value={formData.openrouter_api_key || ''}
              onChange={(e) => updateFormData({ openrouter_api_key: e.target.value || null })}
              placeholder="Your OpenRouter API key"
              disabled={disabled}
            />
          </div>
        )}

        {/* Custom Chat Completion Request Args (JSON) */}
        <div className="form-row form-group">
          <label className="toggle-label">
            Custom Chat Completion Args (JSON)
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={chatArgsEnabled}
                onChange={(e) => setChatArgsEnabled(e.target.checked)}
                disabled={disabled}
                className="toggle-input"
              />
              <span className="toggle-slider"></span>
            </div>
          </label>
          <p className="form-help-text">
            Optional. Example: {"{ \"temperature\": 0.2, \"max_tokens\": 2048 }"}
          </p>
        </div>
        {chatArgsEnabled && (
          <div className="form-row form-group">
            <textarea
              value={chatArgsText}
              onChange={(e) => handleChatArgsChange(e.target.value)}
              onKeyDown={handleChatArgsKeyDown}
              placeholder='Enter JSON object, e.g. {"temperature": 0.2}'
              disabled={disabled}
              rows={6}
              style={{ resize: 'vertical', fontFamily: 'monospace' }}
            />
            {chatArgsError && <small style={{ color: '#dc2626' }}>{chatArgsError}</small>}
          </div>
        )}
      </div>

      <div className="form-section">
        <h3>Document Type Definitions *</h3>
        
        <div className="template-section">
          <h4>Quick Templates</h4>
          <div className="template-buttons">
            {Object.entries(DOCUMENT_TYPE_TEMPLATES).map(([key, template]) => (
              <button
                key={key}
                type="button"
                onClick={() => loadTemplate(key as keyof typeof DOCUMENT_TYPE_TEMPLATES)}
                className="btn btn-template"
                disabled={disabled}
              >
                {template.displayName}
              </button>
            ))}
          </div>
        </div>

        {formData.document_type_definitions.map((docType, index) => (
          <div key={index} className="document-type-row">
            <div className="form-group flex-grow">
              <input
                type="text"
                value={docType.name}
                onChange={(e) => updateDocumentType(index, 'name', e.target.value)}
                placeholder="Document type name"
                required
                disabled={disabled}
              />
            </div>
            <div className="form-group flex-grow">
              <textarea
                value={docType.definition || ''}
                onChange={(e) => updateDocumentType(index, 'definition', e.target.value)}
                placeholder="Document type definition (optional)"
                disabled={disabled}
                rows={2}
                style={{ resize: 'vertical' }}
              />
            </div>
            {formData.document_type_definitions.length > 1 && (
              <button
                type="button"
                onClick={() => removeDocumentType(index)}
                className="btn btn-danger"
                disabled={disabled}
              >
                Remove
              </button>
            )}
          </div>
        ))}
        <div className="document-type-actions">
          <button
            type="button"
            onClick={addDocumentType}
            className="btn btn-secondary"
            disabled={disabled}
          >
            Add Document Type
          </button>
          {formData.document_type_definitions.length > 1 && (
            <button
              type="button"
              onClick={removeAllDocumentTypes}
              className="btn btn-danger"
              disabled={disabled}
            >
              Delete All
            </button>
          )}
        </div>
      </div>

      {features.enableAdvancedOptions && (
        <div className="form-section">
          <h3>Processing Options</h3>
          <div className="form-row">
            <div className="form-group">
              <label>PDF DPI</label>
              <input
                type="number"
                value={formData.pdf_dpi}
                onChange={(e) => updateFormData({ pdf_dpi: parseInt(e.target.value) })}
                min="72"
                max="300"
                disabled={disabled}
              />
            </div>
            <div className="form-group">
              <label>Leading Line Limit</label>
              <input
                type="number"
                value={formData.leading_line_limit}
                onChange={(e) => updateFormData({ leading_line_limit: parseInt(e.target.value) })}
                min="1"
                disabled={disabled}
              />
            </div>
            <div className="form-group">
              <label>Trailing Line Limit</label>
              <input
                type="number"
                value={formData.trailing_line_limit}
                onChange={(e) => updateFormData({ trailing_line_limit: parseInt(e.target.value) })}
                min="1"
                disabled={disabled}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.auto_adjust_image_size}
                  onChange={(e) => updateFormData({ auto_adjust_image_size: e.target.checked })}
                  disabled={disabled}
                />
                Auto Adjust Image Size
              </label>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.add_prompt}
                  onChange={(e) => updateFormData({ add_prompt: e.target.checked })}
                  disabled={disabled}
                />
                Add Prompt to Response
              </label>
            </div>
          </div>
        </div>
      )}
    </form>
  );
});